import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "./hooks/useLanguage";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { AuthProvider } from "@/contexts/AuthContext";
import React, { Suspense, lazy } from 'react';

console.log("📦 App.tsx - React object:", React);
console.log("📦 App.tsx - React.forwardRef:", !!React.forwardRef);
import SimpleProtectedRoute from '@/components/auth/SimpleProtectedRoute';
import { Skeleton } from "@/components/ui/skeleton";
import ErrorBoundary from '@/components/ErrorBoundary';

// Lazy load all route components for better code splitting
const Index = lazy(() => import("./pages/Index"));
const Members = lazy(() => import("./pages/Members"));
const Networking = lazy(() => import("./pages/Networking"));
const MemberAccess = lazy(() => import("./pages/MemberAccess"));
const Services = lazy(() => import("./pages/Services"));
const ClubDashboard = lazy(() => import("./pages/ClubDashboard"));
const ClubManagement = lazy(() => import("./pages/ClubManagement"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminLayout = lazy(() => import('@/components/admin/AdminLayout'));
const AdminMembers = lazy(() => import('@/pages/admin/Members'));
const AdminDashboard = lazy(() => import('@/pages/admin/Dashboard'));
const Subscriptions = lazy(() => import('@/pages/admin/Subscriptions'));
const Analytics = lazy(() => import('@/pages/admin/Analytics'));
const Events = lazy(() => import('@/pages/admin/Events'));
const Settings = lazy(() => import('@/pages/admin/Settings'));
const Communications = lazy(() => import('@/pages/backoffice/Communications'));
const Notifications = lazy(() => import('@/pages/backoffice/Notifications'));
const BackofficeSettings = lazy(() => import('@/pages/backoffice/Settings'));
const DatabaseManagement = lazy(() => import('@/pages/admin/Database'));
const SecurityCenter = lazy(() => import('@/pages/admin/Security'));
const Reports = lazy(() => import('@/pages/admin/Reports'));
const Workflows = lazy(() => import('@/pages/admin/Workflows'));
const AdminWorkshops = lazy(() => import('@/pages/admin/Workshops'));
const AdminInsights = lazy(() => import('@/pages/admin/Insights'));
const AdminOpportunities = lazy(() => import('@/pages/admin/Opportunities'));
const AdminNetworking = lazy(() => import('@/pages/admin/Networking'));
const AdminPerks = lazy(() => import('@/pages/admin/Perks'));
const CRM = lazy(() => import('@/pages/admin/CRM'));
const MemberEvents = lazy(() => import('@/pages/Events'));
const Opportunities = lazy(() => import('@/pages/Opportunities'));
const Insights = lazy(() => import('@/pages/Insights'));
const InsightDetail = lazy(() => import('@/pages/InsightDetail'));
const Workshops = lazy(() => import('@/pages/Workshops'));
const MemberRequests = lazy(() => import('@/pages/admin/MemberRequests'));
const Subscription = lazy(() => import('@/pages/Subscription'));
const SubscriptionSuccess = lazy(() => import('@/pages/SubscriptionSuccess'));
const Users = lazy(() => import('@/pages/admin/Users'));
const Profile = lazy(() => import('./pages/Profile'));
const StripeSettings = lazy(() => import('./pages/admin/StripeSettings'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AdminInvoices = lazy(() => import('./pages/admin/Invoices'));
const Formation = lazy(() => import('./pages/Formation'));
const Business = lazy(() => import('./pages/Business'));
const International = lazy(() => import('./pages/International'));
const Deals = lazy(() => import('./pages/Deals'));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="space-y-4 w-full max-w-md mx-auto p-4">
      <Skeleton className="h-8 w-3/4 mx-auto" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-32 w-full" />
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

// Ancien ProtectedRoute supprimé - remplacé par SimpleProtectedRoute

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <AuthProvider>
            <SubscriptionProvider>
              <LanguageProvider>
                <Toaster />
                <Sonner />
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/member-access" element={<MemberAccess />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/subscription" element={<Subscription />} />
                  <Route path="/subscription-success" element={<SubscriptionSuccess />} />
                  <Route path="/profile" element={
                    <SimpleProtectedRoute requiredLevel="member">
                      <Profile />
                    </SimpleProtectedRoute>
                  } />
                  <Route path="/members" element={
                    <SimpleProtectedRoute requiredLevel="member">
                      <Members />
                    </SimpleProtectedRoute>
                  } />
                  <Route path="/networking" element={
                    <SimpleProtectedRoute requiredLevel="member">
                      <Networking />
                    </SimpleProtectedRoute>
                  } />
                  <Route path="/events" element={
                    <SimpleProtectedRoute requiredLevel="member">
                      <MemberEvents />
                    </SimpleProtectedRoute>
                  } />
                  <Route path="/opportunities" element={
                    <SimpleProtectedRoute requiredLevel="member">
                      <Opportunities />
                    </SimpleProtectedRoute>
                  } />
                  <Route path="/insights" element={
                    <SimpleProtectedRoute requiredLevel="member">
                      <Insights />
                    </SimpleProtectedRoute>
                  } />
                  <Route path="/insights/:id" element={
                    <SimpleProtectedRoute requiredLevel="member">
                      <InsightDetail />
                    </SimpleProtectedRoute>
                  } />
                  <Route path="/workshops" element={
                    <SimpleProtectedRoute requiredLevel="member">
                      <Workshops />
                    </SimpleProtectedRoute>
                  } />
                  <Route path="/perks" element={<Navigate to="/deals" replace />} />
                  <Route path="/formation" element={
                    <SimpleProtectedRoute requiredLevel="member">
                      <Formation />
                    </SimpleProtectedRoute>
                  } />
                  <Route path="/business" element={
                    <SimpleProtectedRoute requiredLevel="member">
                      <Business />
                    </SimpleProtectedRoute>
                  } />
                  <Route path="/deals" element={
                    <SimpleProtectedRoute requiredLevel="member">
                      <Deals />
                    </SimpleProtectedRoute>
                  } />
                  <Route path="/international" element={
                    <SimpleProtectedRoute requiredLevel="member">
                      <International />
                    </SimpleProtectedRoute>
                  } />
                  <Route path="/dashboard" element={
                    <SimpleProtectedRoute requiredLevel="member">
                      <Dashboard />
                    </SimpleProtectedRoute>
                  } />
                  <Route path="/club-dashboard" element={
                    <SimpleProtectedRoute requiredLevel="member">
                      <ClubDashboard />
                    </SimpleProtectedRoute>
                  } />
                  <Route path="/club-management" element={
                    <SimpleProtectedRoute requiredLevel="member">
                      <ClubManagement />
                    </SimpleProtectedRoute>
                  } />
                  <Route path="/admin" element={
                    <SimpleProtectedRoute requiredLevel="admin">
                      <AdminLayout />
                    </SimpleProtectedRoute>
                  }>
                    <Route index element={<AdminDashboard />} />
                    <Route path="members" element={<AdminMembers />} />
                    <Route path="crm" element={<CRM />} />
                    <Route path="users" element={<Users />} />
                    <Route path="subscriptions" element={<Subscriptions />} />
                    <Route path="analytics" element={<Analytics />} />
                    <Route path="events" element={<Events />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="member-requests" element={<MemberRequests />} />
                    <Route path="communications" element={<Communications />} />
                    <Route path="notifications" element={<Notifications />} />
                    <Route path="backoffice-settings" element={<BackofficeSettings />} />
                    <Route path="reports" element={<Reports />} />
                    <Route path="workflows" element={<Workflows />} />
                    <Route path="workshops" element={<AdminWorkshops />} />
                    <Route path="perks" element={<AdminPerks />} />
                    <Route path="insights" element={<AdminInsights />} />
                    <Route path="opportunities" element={<AdminOpportunities />} />
                    <Route path="networking" element={<AdminNetworking />} />
                    <Route path="database" element={<DatabaseManagement />} />
                    <Route path="security" element={<SecurityCenter />} />
                    <Route path="stripe-settings" element={<StripeSettings />} />
                    <Route path="invoices" element={<AdminInvoices />} />
                  </Route>
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
                </ErrorBoundary>
            </LanguageProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
